<template>
  <div class="search-result-map">
    <nav class="item-search-map-result-nav" role="toolbar">
      <div class="hitsize">
        <h2 class="jps-is-hidden">
          {{ lss('検索結果件数', 'Search Result Number') }}
        </h2>
        <p class="is-size-7-touch">
          {{ hitMessage }}
        </p>
        <label class="select-label">{{ lss('最大表示件数', 'Max') }}:</label>
        <SearchPagesize :values="[100, 500, 1000]" :ss="ss" />
      </div>
      <div class="hitmap">
        <div class="radius">
          <label class="select-label">
            {{ lss('検索する半径', 'Radius') }}:</label
          >
          <BaseSelect
            class="jps-round-select jps-small"
            v-model="distanceMode"
            :options="distanceOptions"
            :label-function="distanceDisplay"
          />
          <BaseCheckbox v-model="merge">{{
            lss('座標が近い資料をまとめる', 'Group nearby items')
          }}</BaseCheckbox>
          <BaseCheckbox v-model="autoSearch">{{
            lss('地図を動かしたら自動で再検索', 'Auto')
          }}</BaseCheckbox>
        </div>
      </div>
    </nav>
    <div class="item-search-result-body" role="main" aria-live="assertive">
      <h2 class="jps-is-hidden">
        {{ lss('検索結果地図', 'Search ResultMap') }}
      </h2>
      <div class="map-result">
        <ClientOnly>
          <UiMap
            ref="uiMap"
            :mode="merge ? 'MERGE' : 'NORMAL'"
            :points="points"
            :circle-param="circleParam"
            @move="moveMap()"
            @click-point="clickPoint($event)"
          />
        </ClientOnly>
        <button class="research jps-round-button jps-red" @click="setGeo()">
          <BaseIcon size="size-6" name="mdi/crosshairs-gps" />
          {{ lss('このエリアを検索', 'Search') }}
        </button>
        <span class="map-center">
          <BaseIcon size="size-4" name="mdi/crosshairs-gps" />
        </span>
        <div v-if="selectItem" class="select-item-meta is-hidden-tablet">
          <div class="media">
            <div class="media-content">
              <div class="content is-size-7">
                <div class="item-title-container is-size-6">
                  <strong>
                    <BaseLink
                      :nolink="isWebParts"
                      @click.native="emits('show', selectItem)"
                      :to="itemTo(selectItem)"
                      >{{
                        selectItem.common.title
                          ? selectItem.common.title
                          : ls(dapLangGlobal.noTitle)
                      }}</BaseLink
                    >
                  </strong>
                  <UiButtonIcon
                    tag="button"
                    class="jps-icon-button"
                    @click="selectItem = null"
                    icon="mdi/close"
                  />
                </div>
                <div class="item-meta">
                  <p class="jps-item-details">
                    {{ ljoinLists(selectItem.common.location) }}
                  </p>
                  <div class="level item-database">
                    <p class="level-left">
                      <BaseLink
                        class="item-common level"
                        :nolink="isWebParts"
                        :to="'/database/' + selectItem.common.database"
                      >
                        <BaseIcon name="mdi/database" class="is-size-6" />
                        <UiDbSpan :id="selectItem.common.database" />
                      </BaseLink>
                    </p>
                  </div>
                  <p class="jps-item-desc">
                    {{ abbText(lid(selectItem), 100) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="map-result-list" ref="mapList" v-if="ss.searched">
        <div
          class="media"
          :id="'id_' + item.id"
          v-for="item in ss.state.result.list"
          :key="item.id"
        >
          <div class="media-content">
            <div class="content is-size-7">
              <div class="item-title-container is-size-6">
                <strong>
                  <BaseLink
                    :nolink="isWebParts"
                    :to="itemTo(item)"
                    >{{
                      item.common.title ? item.common.title : ls(dapLangGlobal.noTitle)
                    }}</BaseLink
                  >
                </strong>
              </div>
              <div class="item-meta">
                <p class="jps-item-details">
                  {{ ljoinLists(item.common.location) }}
                </p>
                <div class="level item-database">
                  <p class="level-left">
                    <BaseLink
                      class="item-common level"
                      :nolink="isWebParts"
                      :to="'/database/' + item.common.database"
                    >
                      <BaseIcon name="mdi/database" class="is-size-6" />
                      <UiDbSpan :id="item.common.database" />
                    </BaseLink>
                  </p>
                </div>
                <p class="jps-item-desc">{{ abbText(lid(item), 100) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { UiMap } from '#components'
import { useSearchResult } from '~/components/ui/item/search/useSearchResult'

const { ls, lss, lst, li, num, lid, ljoinLists } = useI18n()
const { isWebParts } = useWebParts()

interface Props {
  test?: boolean
  ss: SearchStore<Item>
}
const props = withDefaults(defineProps<Props>(), {
  test: false
})

const emits = defineEmits(['show'])

const { itemTo } = useSearchResult(props.ss, props.test)

let debouncedSearch: any = null
onBeforeMount(() => {
  props.ss.addAfterSearchListener(af => {
    selectItem.value = null
  })
  debouncedSearch = props.ss.createDebouncedExecute(800)
})

const hitMessage = computed(() => {
  if (props.ss.state.result) {
    if (props.ss.state.result.hit === props.ss.state.result.list.length) {
      return lst(
        { ja: '{}件見つかりました。', en: '{} items found' },
        num(props.ss.state.result.hit),
      )
    } else {
      return lst(
        {
          ja: '{}件中、{}件を表示しています。',
          en: 'From {} hit, show {} items',
        },
        num(props.ss.state.result.hit),
        num(props.ss.state.result.list.length),
      )
    }
  } else {
    return ''
  }
})

//TODO common以外の座標を拾う？ (元々存在するTODO)
const points = computed(() => {
  if (props.ss && props.ss.state.result) {
    let getImage = (item) => {
      if (props.ss.state.result.hit >= 500) return null
      if (isEmptyArray(item.common.thumbnailUrl)) return null
      return item.common.thumbnailUrl[0]
    }
    return props.ss.state.result.list
      .filter(r => r.common.coordinates)
      .map(r => {
        return {
          lat: r.common.coordinates.lat,
          lon: r.common.coordinates.lon,
          image: getImage(r),
          id: r.id,
          label: abbText(li(r), 30),
        }
      })
  } else {
    return []
  }
})

const merge: Ref<boolean> = ref(true)
const autoSearch: Ref<boolean> = ref(false)

const distanceMode = ref('auto')
const distanceOptions = ref(['auto', '10', '50', '100', '200'])
const distanceDisplay = (v) => {
  if (v === 'auto') return lss('自動', 'Auto')
  else return v + 'km'
}

watch(
  () => distanceMode.value,
  () => {
    setGeo()
  },
)

const mapList = ref<HTMLElement>(null)
const smoothScroll = inject('smoothScroll') as (payload?: any) => void
const clickPoint = async (ev: { id: string }) => {
  nextTick(() => {
    smoothScroll({
      scrollTo: document.getElementById('#id_' + ev.id),
      duration: 250,
      container: mapList.value,
    })
  })
  let item = props.ss.state.result.list.filter(i => i.id === ev.id).pop()
  if (selectItem.value == item) {
    try {
      document
        .getElementById('id_' + ev.id)
        .getElementsByTagName('a')[0]
        .click()
    } catch (e) {}
  } else {
    selectItem.value = item
  }
}

const circleParam = computed(() => {
  const geoKey = Object.keys(props.ss.state.geoDistance).pop()
  if (
    geoKey &&
    props.ss.state.geoDistance[geoKey] &&
    props.ss.state.geoDistance[geoKey].length > 0
  ) {
    return props.ss.state.geoDistance[geoKey].map(gd => {
      return {
        lat: gd.lat,
        lon: gd.lon,
        radius: Number(gd.distance.replace('km', '')),
      }
    })[0]
  }
  return null
})

const uiMap = ref<InstanceType<typeof UiMap>>()
const setGeo = () => {
  if (!uiMap.value) return
  let view = uiMap.value.getView()
  let dist = 0
  if (distanceMode.value === 'auto') {
    const height = view.element.offsetHeight
    dist = Math.ceil((height * 30) / Math.pow(2, view.zoom - 1))
  } else {
    dist = Number(distanceMode.value)
  }
  const geoKey = Object.keys(props.ss.state.geoDistance).pop()
  if (geoKey) {
    props.ss.state.sort = ['geo:' + geoKey]
    props.ss.setGeo(
      geoKey,
      {
        lat: view.latlon.lat,
        lon: view.latlon.lon,
        distance: dist + 'km',
      },
      false,
    )
    debouncedSearch()
  }
}

const moveMap = () => {
  if (autoSearch.value) {
    setGeo()
  }
}

const selectItem: Ref<Item> = ref(null)
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.search-result-map {
  position: relative;
  nav.item-search-map-result-nav {
    @include is-mobile() {
      align-items: flex-start !important;
      margin-bottom: 1rem !important;
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
    }
    .radius,
    .research,
    .hitmap,
    .hitsize {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      .jps-checkbox {
        margin-left: 0.5rem !important;
      }
      @include is-mobile() {
        flex-direction: column;
        align-items: flex-start;
        * {
          margin-left: 0;
        }
      }
    }
    .hitsize {
      gap: 0.25rem;
      margin-bottom: 1rem;
      .select-label {
        margin-left: 1rem;
        @include is-mobile {
          margin-left: 0;
        }
      }
    }
    .hitmap {
      @include box-shadow;
      position: absolute;
      z-index: 1000;
      right: 1rem;
      top: calc(2rem + 26px);
      align-items: flex-start;
      flex-direction: column;
      gap: 1rem;
      border-radius: $global-radius-large;
      padding: 1rem;
      border: 1px solid $jps-gray-100;
      background: $white;
      .radius {
        display: grid;
        grid-template-columns: 6rem 1fr;
        gap: 0.5rem;
        .jps-checkbox {
          grid-column: 1 / 3;
        }
      }
      @include is-mobile() {
        align-items: flex-start;
        position: relative;
        top: auto;
        right: auto;
        width: 100%;
      }
    }
  }

  .item-search-result-body {
    height: 80vh;

    position: relative;

    display: flex;
    flex-direction: row-reverse;

    .map-result {
      flex-grow: 1;
      position: relative;
      .jps-map {
        height: 80vh;
      }
      .research {
        position: absolute;
        left: calc(50% - 75px);
        width: 150px;
        top: 1rem;
        /* background-color: $jps-gray-100; */
        @include box-shadow;
        z-index: 999;
        .jps-icon {
          /* color: $jps-key-blue; */
        }
      }
      .map-center {
        position: absolute;
        left: calc(50% - 5px);
        top: calc(50% - 5px);
        width: 10px;
        height: 10px;
        pointer-events: none;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999;
      }
      .select-item-meta {
        position: absolute;
        width: 100%;
        bottom: 0;
        left: 0;
        z-index: 1000;
        padding: 0.5rem;
        background: rgba(256, 256, 256, 0.8);
        .item-title-container {
          display: flex;
          .jps-icon-button {
            margin-left: auto;
            align-items: center;
          }
        }
      }
    }

    .map-result-list {
      overflow-y: auto;
      padding: 1rem;
      width: 24rem;
      @include is-mobile() {
        display: none;
      }
      @include is-tablet() {
        width: 16rem;
      }
      @include is-desktop() {
        width: 24rem;
      }
    }
  }
}
</style>
